/**
 * 客户池评分页面（公共池评分排名）
 *
 * 展示客户线索的综合评分排名列表，用于销售团队快速筛选高价值客户。
 * 评分体系结合规则评分和 NLP/LLM 评分，最终计算综合总分用于排序。
 *
 * 核心功能：
 *   - 统计卡片区：展示客户总数、平均评分、高分/低分客户数（后端聚合全量数据）
 *   - 多维度筛选：评分快捷按钮 + 滑块、意向等级、城市、评分规则、回捞次数
 *   - 评分排名表格：前3名金银铜徽章、圆形评分徽章、关键词标签
 *   - 评分详情弹窗：卡片式分区展示维度得分、意向分析、命中关键词、跟进记录
 *   - 批量分配：勾选客户后批量分配给销售人员
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table, Button, Form, Modal, Slider, Tag, Space, Toast, Input, Select, Typography, Divider,
} from "@douyinfe/semi-ui";
import { FormApi } from "@douyinfe/semi-ui/lib/es/form";
import { ApiClient } from "../../api/ApiClient";
import { formatAmountZhCn, formatDateTime, displayStar } from "../../utils";
import { LeadDetail } from "./leadDetail";
import ScoringKeywordConfig from "./scoringKeywordConfig";

const { Text } = Typography;
const apiClient = ApiClient.getDefault();

/* ==================== 类型定义 ==================== */

interface LeadScoringResultRecord {
  id: number;
  leadId: number;
  scoreType: string;
  totalScore: number;
  ruleScore: number;
  nlpScore: number;
  llmScore: number;
  scoringDetail: string;
  hitPositive: string[];
  hitNegative: string[];
  dimensionScores: Record<string, number>;
  intentLevel: number;
  intentLabel: string;
  reassignCount: number;
  remarkVersion: number;
  updatedAt: string;
  name: string;
  fullMobile: string;
  applyCity: string;
  applyAmount: number;
  star: number;
  assignCount: number;
  remark: string;
  house: string;
  vehicle: string;
  providentFund: string;
  income: string;
  job: string;
  source: string;
  contactLogs: string[];
  intentSummary: any;
}

interface PoolStats {
  totalCount: number;
  avgScore: number;
  highScoreCount: number;
  mediumScoreCount: number;
  lowScoreCount: number;
}

/* ==================== 配置常量 ==================== */

const INTENT_CONFIG: Record<number, { label: string; color: string }> = {
  0: { label: "无法触达", color: "grey" },
  1: { label: "无意向", color: "red" },
  2: { label: "待跟进", color: "orange" },
  3: { label: "有意向", color: "blue" },
  4: { label: "高意向", color: "green" },
  5: { label: "成交中", color: "purple" },
};

const INTENT_OPTIONS = [
  { value: -1, label: "全部意向" },
  { value: 0, label: "0 - 无法触达" },
  { value: 1, label: "1 - 无意向" },
  { value: 2, label: "2 - 待跟进" },
  { value: 3, label: "3 - 有意向" },
  { value: 4, label: "4 - 高意向" },
  { value: 5, label: "5 - 成交中" },
];

/* ==================== 工具函数 ==================== */

const getScoreColor = (score: number): string => {
  if (score >= 60) return "var(--semi-color-success)";
  if (score >= 40) return "var(--semi-color-warning)";
  return "var(--semi-color-danger)";
};

const getScoreHexColor = (score: number): string => {
  if (score >= 60) return "#28a745";
  if (score >= 40) return "#faad14";
  return "#ff4d4f";
};

/** 排名徽章：前3名金银铜，其余普通数字 */
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank <= 3) {
    const cfg = [
      { bg: "#fff7e6", border: "#ffd700", text: "#b8860b" },
      { bg: "#f5f5f5", border: "#c0c0c0", text: "#666" },
      { bg: "#fff2e8", border: "#cd7f32", text: "#8b4513" },
    ][rank - 1];
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: "50%",
        background: cfg.bg, border: `2px solid ${cfg.border}`,
        color: cfg.text, fontWeight: 700, fontSize: 12,
      }}>{rank}</span>
    );
  }
  return <Text type="tertiary" style={{ fontSize: 13 }}>{rank}</Text>;
};

/** 圆形评分徽章 */
const ScoreBadge: React.FC<{ score: number }> = ({ score }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 40, height: 40, borderRadius: "50%",
    background: `${getScoreHexColor(score)}15`,
    border: `2px solid ${getScoreHexColor(score)}`,
    color: getScoreHexColor(score), fontWeight: 700, fontSize: 14,
  }}>{score}</span>
);

/** 关键词高亮 */
const highlightKeywords = (text: string, keywords: string[]): React.ReactNode => {
  if (!text || keywords.length === 0) return text;
  let result: React.ReactNode[] = [text];
  keywords.forEach((kw) => {
    const next: React.ReactNode[] = [];
    result.forEach((part) => {
      if (typeof part !== "string") { next.push(part); return; }
      const segs = part.split(kw);
      segs.forEach((p, i) => {
        if (p) next.push(p);
        if (i < segs.length - 1) {
          next.push(<span key={`${kw}-${i}`} style={{ background: "#fff3cd", padding: "0 2px", borderRadius: 2 }}>{kw}</span>);
        }
      });
    });
    result = next;
  });
  return <>{result}</>;
};

/** 安全解析意向分析 JSON */
const parseIntentSummary = (raw: any): any => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

/* ==================== 统计卡片样式 ==================== */

const statCardStyle = (accentColor: string): React.CSSProperties => ({
  flex: 1, minWidth: 150, padding: "16px 20px",
  background: "var(--semi-color-bg-0)", borderRadius: 8,
  border: "1px solid var(--semi-color-border)", borderLeft: `3px solid ${accentColor}`,
});

const sectionCardStyle: React.CSSProperties = {
  background: "var(--semi-color-fill-0)", borderRadius: 8, padding: 16, marginBottom: 12,
};

/* ==================== 主组件 ==================== */

export default function PoolScoring() {
  // 表格数据
  const [data, setData] = useState<LeadScoringResultRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // 筛选条件
  const [minScore, setMinScore] = useState(0);
  const [city, setCity] = useState("");
  const [intentLevel, setIntentLevel] = useState<number | undefined>();
  const [scoreType, setScoreType] = useState<string | undefined>();
  const [minReassignCount, setMinReassignCount] = useState<number | undefined>();

  // 选择与操作
  const [selectedRows, setSelectedRows] = useState<LeadScoringResultRecord[]>([]);
  const [scoringLoading, setScoringLoading] = useState(false);

  // 弹窗状态
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState<LeadScoringResultRecord | null>(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [detailLeadId, setDetailLeadId] = useState<number>(0);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignFormApi, setAssignFormApi] = useState<FormApi>();
  const [assignSaving, setAssignSaving] = useState(false);
  const [teamMembersTree, setTeamMembersTree] = useState<any[]>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);

  // 统计数据
  const [stats, setStats] = useState<PoolStats | null>(null);

  // 从 localStorage 恢复上次分配的销售人员
  const selectedMembersRef = useRef<any[]>([]);
  try {
    const saved = localStorage.getItem("assignToIds");
    if (saved) selectedMembersRef.current = JSON.parse(saved);
  } catch {}

  /* ---------- 数据加载 ---------- */

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, size: pageSize, minScore };
      if (city) params.city = city;
      if (intentLevel !== undefined) params.intentLevel = intentLevel;
      if (scoreType) params.scoreType = scoreType;
      if (minReassignCount !== undefined && minReassignCount > 0) params.minReassignCount = minReassignCount;
      const resp = await apiClient.request<{ current: number; total: number; records: LeadScoringResultRecord[] }>(
        "/leadScoring/pool", "GET", params
      );
      if (resp.data) {
        setData(resp.data.records || []);
        setTotal(resp.data.total || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, pageSize, minScore, city, intentLevel, scoreType, minReassignCount]);

  /** 加载统计数据（全量聚合，与分页无关） */
  const loadStats = useCallback(async () => {
    try {
      const params: Record<string, any> = { minScore };
      if (city) params.city = city;
      if (intentLevel !== undefined) params.intentLevel = intentLevel;
      if (scoreType) params.scoreType = scoreType;
      if (minReassignCount !== undefined && minReassignCount > 0) params.minReassignCount = minReassignCount;
      const resp = await apiClient.request<PoolStats>("/leadScoring/pool/stats", "GET", params);
      if (resp.data) setStats(resp.data);
    } catch (e) { console.error(e); }
  }, [minScore, city, intentLevel, scoreType, minReassignCount]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const username = localStorage.getItem("username") || "";
    apiClient.getTeamMemberTreeAsOptsOfAny(username).then((opts) => setTeamMembersTree(opts));
  }, []);

  /* ---------- 事件处理 ---------- */

  const handleViewDetail = async (record: LeadScoringResultRecord) => {
    try {
      const params: Record<string, any> = { leadId: record.leadId };
      if (record.scoreType) params.scoreType = record.scoreType;
      const resp = await apiClient.request<LeadScoringResultRecord>("/leadScoring/detail", "GET", params);
      if (resp.data) { setDetailData(resp.data); setShowDetailModal(true); }
    } catch (e) { console.error(e); }
  };

  const handleScore = async (type: string) => {
    if (selectedRows.length === 0) { Toast.warning("请先选择客户"); return; }
    setScoringLoading(true);
    try {
      const leadIds = selectedRows.map((r) => r.leadId);
      if (leadIds.length === 1) {
        await apiClient.request("/leadScoring/score", "POST", { leadId: leadIds[0], scoreType: type });
      } else {
        await apiClient.request("/leadScoring/score/batch", "POST", { scoreType: type }, leadIds);
      }
      Toast.success("评分完成");
      setSelectedRows([]);
      loadData();
      loadStats();
    } catch (e: any) { Toast.error(e?.message || "评分失败"); }
    finally { setScoringLoading(false); }
  };

  const handleAssignClick = () => {
    if (selectedRows.length === 0) { Toast.warning("请先选择客户"); return; }
    setShowAssignModal(true);
  };

  const doAssign = async () => {
    setAssignSaving(true);
    try {
      await assignFormApi!.validate();
      const formValues = assignFormApi!.getValues();
      await apiClient.request("/leadScoring/assign", "POST", undefined, {
        leadIds: selectedRows.map((r) => r.leadId),
        assignToIds: formValues.assignToIds,
        assignLeadType: formValues.assignLeadType || "my",
      });
      localStorage.setItem("assignToIds", JSON.stringify(formValues.assignToIds));
      Toast.success("分配成功");
      setShowAssignModal(false);
      setSelectedRows([]);
      loadData();
      loadStats();
    } catch (e: any) { Toast.error(e?.message || "分配失败"); }
    finally { setAssignSaving(false); }
  };

  /* ---------- 表格列定义 ---------- */

  const columns = [
    {
      title: "排名", dataIndex: "index", width: 60,
      render: (_: any, __: any, index: number) => <RankBadge rank={(page - 1) * pageSize + index + 1} />,
    },
    {
      title: "客户", dataIndex: "name", width: 90,
      render: (value, record, index) => {
              return (
                <Typography.Text
                    link
                  onClick={() => detailAction(record)}
                >
                  {value}({record.leadId})
                </Typography.Text>
              );
            },
    },
    {
      title: "综合评分", dataIndex: "totalScore", width: 80, sorter: true,
      render: (score: number) => <ScoreBadge score={score} />,
    },
    {
      title: "意向", dataIndex: "intentLevel", width: 80,
      render: (level: number) => {
        const cfg = INTENT_CONFIG[level];
        return cfg ? <Tag size="small" color={cfg.color as any}>{cfg.label}</Tag> : "-";
      },
    },
    {
      title: "资质", width: 150,
      render: (_: any, r: LeadScoringResultRecord) => (
        <Space style={{ gap: 4 }}>
          {r.house && r.house !== "无房" && <Tag size="small" color="blue">{r.house}</Tag>}
          {r.vehicle && r.vehicle !== "无车" && <Tag size="small" color="cyan">{r.vehicle}</Tag>}
          {r.income && <Tag size="small">{r.income}</Tag>}
        </Space>
      ),
    },
    {
      title: "申请金额", dataIndex: "applyAmount", width: 90,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmountZhCn(v)}</span>,
    },
    {
      title: "星级", dataIndex: "star", width: 55,
      render: (v: number) => displayStar(v),
    },
    {
      title: "规则", dataIndex: "scoreType", width: 65,
      render: (v: string) => v ? <Tag size="small" color={v === "LLM" ? "purple" : "blue"}>{v}</Tag> : "-",
    },
    {
      title: "评分时间", dataIndex: "updatedAt", width: 120,
      render: (v: string) => v ? formatDateTime(v) : "-",
    },
    {
      title: "命中关键词", dataIndex: "hitPositive", width: 180,
      render: (_: any, r: LeadScoringResultRecord) => {
        const all = [
          ...(r.hitPositive || []).map((k) => ({ k, c: "green" })),
          ...(r.hitNegative || []).map((k) => ({ k, c: "red" })),
        ].slice(0, 5);
        return (
          <Space style={{ gap: 2 }} wrap>
            {all.map((item, i) => <Tag key={i} size="small" color={item.c as any}>{item.k}</Tag>)}
            {all.length >= 5 && <Tag size="small">...</Tag>}
          </Space>
        );
      },
    },
    {
      title: "操作", width: 80, fixed: "right" as const,
      render: (_: any, record: LeadScoringResultRecord) => (
        <Button theme="borderless" size="small" onClick={() => handleViewDetail(record)}>详情</Button>
      ),
    },
  ];

  //打开详情
    const detailAction = (a: any) => {
      const w = window.open("about:blank");
      if (w != null) {
        const data = {
          id: a.leadId,
          ids: [],
    
        };
        // w.location.href =
        //   "/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify(data));
         w.location.href =
          "/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify(data));
      }
    };

  /* ==================== JSX ==================== */

  return (
    <div style={{ padding: 20, background: "var(--semi-color-bg-1)", minHeight: "100%" }}>

      {/* ====== 统计卡片区 ====== */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={statCardStyle("var(--semi-color-primary)")}>
          <Text type="tertiary" size="small">客户总数</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-text-0)", marginTop: 4 }}>
            {stats?.totalCount ?? "-"}
          </div>
        </div>
        <div style={statCardStyle(getScoreColor(stats?.avgScore ?? 50))}>
          <Text type="tertiary" size="small">平均评分</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: getScoreColor(stats?.avgScore ?? 50), marginTop: 4 }}>
            {stats?.avgScore != null ? stats.avgScore.toFixed(1) : "-"}
          </div>
        </div>
        <div style={statCardStyle("var(--semi-color-success)")}>
          <Text type="tertiary" size="small">高分客户 (≥60)</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-success)", marginTop: 4 }}>
            {stats?.highScoreCount ?? "-"}
          </div>
        </div>
        <div style={statCardStyle("var(--semi-color-danger)")}>
          <Text type="tertiary" size="small">低分客户 (&lt;40)</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-danger)", marginTop: 4 }}>
            {stats?.lowScoreCount ?? "-"}
          </div>
        </div>
      </div>

      {/* ====== 筛选栏 ====== */}
      <div style={{
        background: "var(--semi-color-bg-0)", borderRadius: 8, padding: "14px 20px",
        marginBottom: 12, border: "1px solid var(--semi-color-border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {/* 评分快捷按钮 */}
          <Space>
            <Button size="small" type={minScore === 0 ? "primary" : "tertiary"}
              onClick={() => { setMinScore(0); setPage(1); }}>全部</Button>
            <Button size="small" type={minScore === 60 ? "primary" : "tertiary"}
              onClick={() => { setMinScore(60); setPage(1); }}
              style={{ color: minScore === 60 ? "#fff" : "#28a745" }}>高分(60+)</Button>
            <Button size="small" type={minScore === 40 ? "primary" : "tertiary"}
              onClick={() => { setMinScore(40); setPage(1); }}>中等(40+)</Button>
          </Space>
          <Divider layout="vertical" />
          {/* 最低分滑块 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text size="small" type="tertiary">最低分:</Text>
            <Slider value={minScore} min={0} max={100} step={5} style={{ width: 100 }}
              onChange={(v) => setMinScore(v as number)} onAfterChange={() => setPage(1)} />
            <Text size="small" strong>{minScore}</Text>
          </div>
          <Divider layout="vertical" />
          {/* 意向等级 */}
          <Select placeholder="意向等级" value={intentLevel} style={{ width: 130 }}
            onChange={(v: number) => { setIntentLevel(v !== -1 ? v : undefined); setPage(1); }}
            optionList={INTENT_OPTIONS} />
          {/* 城市 */}
          <Input placeholder="城市" value={city} onChange={setCity} style={{ width: 100 }}
            onEnterPress={() => { setPage(1); }} />
          {/* 评分规则 */}
          <Select placeholder="评分规则" value={scoreType} style={{ width: 110 }}
            onChange={(v: string) => { setScoreType(v || undefined); setPage(1); }}
            optionList={[{ value: "", label: "全部规则" }, { value: "NLP", label: "NLP" }, { value: "LLM", label: "LLM" }]} />
          {/* 回捞次数 */}
          <Select placeholder="回捞次数" value={minReassignCount} style={{ width: 115 }}
            onChange={(v: number) => { setMinReassignCount(v > 0 ? v : undefined); setPage(1); }}
            optionList={[{ value: 0, label: "不限" }, { value: 1, label: "≥1次" }, { value: 2, label: "≥2次" }, { value: 3, label: "≥3次" }]} />
          <Button theme="solid" onClick={() => { setPage(1); }}>查询</Button>
        </div>
      </div>

      {/* ====== 操作栏 ====== */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12, padding: "0 4px",
      }}>
        <Space>
          <Text type="tertiary" size="small">共 {total} 条</Text>
          {selectedRows.length > 0 && (
            <Tag color="blue" size="small">已选 {selectedRows.length} 项</Tag>
          )}
        </Space>
        <Space>
          <Button size="small" loading={scoringLoading} disabled={selectedRows.length === 0}
            onClick={() => handleScore("NLP")}>NLP评分</Button>
          <Button size="small" loading={scoringLoading} disabled={selectedRows.length === 0}
            onClick={() => handleScore("LLM")}>LLM评分</Button>
          <Button size="small" onClick={() => setShowConfigModal(true)}>评分配置</Button>
          <Button theme="solid" disabled={selectedRows.length === 0}
            onClick={handleAssignClick}>分配({selectedRows.length})</Button>
        </Space>
      </div>

      {/* ====== 数据表格 ====== */}
      <Table columns={columns} dataSource={data} loading={loading} rowKey="leadId"
        scroll={{ x: 1100 }}
        pagination={{ currentPage: page, pageSize, total, onPageChange: (p) => setPage(p), showTotal: true }}
        rowSelection={{ onChange: (_, rows) => setSelectedRows(rows as LeadScoringResultRecord[]) }}
        size="small" bordered
      />

      {/* ====== 评分详情弹窗 ====== */}
      <Modal title="评分详情" visible={showDetailModal} onCancel={() => setShowDetailModal(false)}
        footer={null} width={800} style={{ top: 20 }}>
        {detailData && renderScoreDetail(detailData)}
      </Modal>

      {/* ====== 分配弹窗 ====== */}
      <Modal title="分配客户" visible={showAssignModal} okText="分配"
        confirmLoading={assignSaving} onOk={doAssign} onCancel={() => setShowAssignModal(false)}
        style={{ width: 500 }}>
        <div style={{ marginBottom: 12, color: "var(--semi-color-text-2)", fontSize: 13 }}>
          已选择 {selectedRows.length} 位客户
        </div>
        <Form getFormApi={(api) => setAssignFormApi(api)}>
          <Form.TreeSelect placeholder="请选择分配给谁" rules={[{ required: true, message: "请选择" }]}
            style={{ width: "100%" }} field="assignToIds" label="销售"
            checkRelation="unRelated" leafOnly multiple treeData={teamMembersTree}
            initValue={selectedMembersRef.current} filterTreeNode showFilteredOnly
            searchPlaceholder="搜索销售人员" />
          <Form.Select rules={[{ required: true, message: "请选择" }]} initValue="my"
            style={{ width: "100%" }} field="assignLeadType" label="分配客户类型"
            optionList={[{ value: "new", label: "新客户" }, { value: "my", label: "再分配客户" }]} />
        </Form>
      </Modal>

      {/* ====== 客户详情弹窗 ====== */}
      <Modal title="客户详情" visible={showLeadDetail} onCancel={() => setShowLeadDetail(false)}
        footer={null} width={1400} style={{ top: 20 }}>
        {showLeadDetail && <LeadDetail leadId={detailLeadId} />}
      </Modal>

      {/* ====== 评分配置弹窗 ====== */}
      <Modal title="评分规则配置" visible={showConfigModal} onCancel={() => setShowConfigModal(false)}
        footer={null} width={1100} style={{ top: 20 }}>
        <ScoringKeywordConfig />
      </Modal>
    </div>
  );
}

/* ==================== 评分详情渲染 ==================== */

function renderScoreDetail(d: LeadScoringResultRecord) {
  let nlpDimensions: any[] = [];
  try {
    const detail = d.scoringDetail ? JSON.parse(d.scoringDetail) : {};
    nlpDimensions = detail.nlpDimensions || [];
  } catch {}
  const intentData = parseIntentSummary(d.intentSummary);
  const allKeywords = [...(d.hitPositive || []), ...(d.hitNegative || [])];

  return (
    <div>
      {/* 头部：大分数 + 基本信息 */}
      <div style={{ display: "flex", gap: 24, marginBottom: 16, alignItems: "flex-start" }}>
        <div style={{ textAlign: "center", minWidth: 80 }}>
          <ScoreBadge score={d.totalScore} />
          <div style={{ marginTop: 6 }}>
            <Text size="small" type="tertiary">综合评分</Text>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Text strong style={{ fontSize: 18 }}>{d.name || "-"}</Text>
            {INTENT_CONFIG[d.intentLevel] && (
              <Tag color={INTENT_CONFIG[d.intentLevel].color as any}>{INTENT_CONFIG[d.intentLevel].label}</Tag>
            )}
            {d.scoreType && <Tag size="small" color={d.scoreType === "LLM" ? "purple" : "blue"}>{d.scoreType}</Tag>}
            {d.reassignCount > 0 && <Tag color="orange" size="small">回捞{d.reassignCount}次</Tag>}
          </div>
          <Text size="small" type="tertiary">
            {d.applyCity || "-"} · {formatAmountZhCn(d.applyAmount)} · 分配{d.assignCount}次 · {displayStar(d.star)}
          </Text>
        </div>
      </div>

      {/* 评分概览卡片 */}
      <div style={sectionCardStyle}>
        <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>评分概览</Text>
        <Space wrap spacing={8}>
          <Tag size="large" color="blue">规则 {d.ruleScore}</Tag>
          <Tag size="large">NLP {d.nlpScore}</Tag>
          {d.llmScore > 0 && <Tag size="large" color="purple">LLM {d.llmScore}</Tag>}
        </Space>
        {d.dimensionScores && Object.keys(d.dimensionScores).length > 0 && nlpDimensions.length === 0 && (
          <div style={{ marginTop: 8 }}>
            <Space wrap spacing={4}>
              {Object.entries(d.dimensionScores).map(([cat, score]) => (
                <Tag key={cat} color="blue" size="small">{cat}: {score}</Tag>
              ))}
            </Space>
          </div>
        )}
      </div>

      {/* NLP 维度匹配 */}
      {nlpDimensions.length > 0 && (
        <div style={sectionCardStyle}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>NLP 维度匹配</Text>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--semi-color-fill-1)" }}>
                <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--semi-color-border)", width: 80 }}>维度</th>
                <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--semi-color-border)", width: 100 }}>分组</th>
                <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid var(--semi-color-border)", width: 55 }}>得分</th>
                <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--semi-color-border)" }}>命中详情</th>
              </tr>
            </thead>
            <tbody>
              {nlpDimensions.map((dim: any) => {
                const match = (dim.matches || [])[0];
                const isBaseline = match?.direction === "baseline";
                return (
                  <tr key={dim.category}>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--semi-color-border)", fontWeight: 500 }}>
                      {dim.categoryName || dim.category}
                    </td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--semi-color-border)", fontSize: 12, color: "var(--semi-color-text-2)" }}>
                      {(dim.matches || []).map((m: any) => m.groupName).filter(Boolean).join(", ") || "-"}
                    </td>
                    <td style={{
                      padding: "6px 10px", borderBottom: "1px solid var(--semi-color-border)", textAlign: "center",
                      color: dim.score > 0 ? "#28a745" : dim.score < 0 ? "#ff4d4f" : "#999", fontWeight: 600,
                    }}>
                      {dim.score > 0 ? "+" : ""}{dim.score}
                    </td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--semi-color-border)" }}>
                      {isBaseline ? (
                        <Tag size="small" color="orange">基准分({match.groupScorePercent}%)</Tag>
                      ) : (
                        <>
                          {(dim.matches || []).map((m: any, mi: number) => (
                            <span key={mi} style={{ marginRight: 6 }}>
                              <Tag size="small" color={m.direction === "positive" ? "green" : "red"}>{m.matchedText}</Tag>
                              {m.type === "regex" && <span style={{ fontSize: 11, color: "#999" }} title={m.pattern}>[正则]</span>}
                            </span>
                          ))}
                          {(!dim.matches || dim.matches.length === 0) && <span style={{ color: "#ccc" }}>-</span>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 意向分析 */}
      {intentData && (
        <div style={sectionCardStyle}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 10 }}>意向分析</Text>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            <div style={{ padding: "10px 12px", background: "var(--semi-color-bg-0)", borderRadius: 6, textAlign: "center" }}>
              <Text type="tertiary" size="small" style={{ display: "block" }}>最高意向</Text>
              <Tag color={INTENT_CONFIG[intentData.maxIntentLevel]?.color as any} style={{ marginTop: 4 }}>
                {intentData.maxIntentLabel}
              </Tag>
            </div>
            <div style={{ padding: "10px 12px", background: "var(--semi-color-bg-0)", borderRadius: 6, textAlign: "center" }}>
              <Text type="tertiary" size="small" style={{ display: "block" }}>接通率</Text>
              <Text strong style={{ fontSize: 18, color: "var(--semi-color-text-0)" }}>
                {(intentData.contactSuccessRate * 100).toFixed(0)}%
              </Text>
            </div>
            <div style={{ padding: "10px 12px", background: "var(--semi-color-bg-0)", borderRadius: 6, textAlign: "center" }}>
              <Text type="tertiary" size="small" style={{ display: "block" }}>最近联系</Text>
              <Text strong style={{ fontSize: 18, color: "var(--semi-color-text-0)" }}>
                {intentData.daysSinceLastContact}<span style={{ fontSize: 12 }}>天前</span>
              </Text>
            </div>
            <div style={{ padding: "10px 12px", background: "var(--semi-color-bg-0)", borderRadius: 6, textAlign: "center" }}>
              <Text type="tertiary" size="small" style={{ display: "block" }}>联系次数</Text>
              <Text strong style={{ fontSize: 18, color: "var(--semi-color-text-0)" }}>{intentData.totalContacts}</Text>
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <Space wrap>
              <Tag>趋势: {intentData.recentTrend}</Tag>
              {intentData.nextAction && <Tag color="blue">建议: {intentData.nextAction}</Tag>}
            </Space>
          </div>
        </div>
      )}

      {/* 命中关键词 */}
      {((d.hitPositive?.length || 0) > 0 || (d.hitNegative?.length || 0) > 0) && (
        <div style={sectionCardStyle}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>命中关键词</Text>
          <Space wrap spacing={4}>
            {(d.hitPositive || []).map((kw, i) => <Tag key={`p${i}`} size="small" color="green">{kw}</Tag>)}
            {(d.hitNegative || []).map((kw, i) => <Tag key={`n${i}`} size="small" color="red">{kw}</Tag>)}
          </Space>
        </div>
      )}

      {/* 资质备注 */}
      {d.remark && (
        <div style={sectionCardStyle}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>资质备注</Text>
          <div style={{
            background: "var(--semi-color-bg-0)", padding: 12, borderRadius: 6,
            fontSize: 13, maxHeight: 120, overflow: "auto", lineHeight: 1.8,
          }}>
            {highlightKeywords(d.remark, allKeywords)}
          </div>
        </div>
      )}

      {/* 跟进记录 */}
      {d.contactLogs && d.contactLogs.length > 0 && (
        <div style={sectionCardStyle}>
          <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>
            跟进记录 <Tag size="small" color="grey">{d.contactLogs.length}</Tag>
          </Text>
          <div style={{ maxHeight: 220, overflow: "auto" }}>
            {d.contactLogs.slice(0, 10).map((log, i) => (
              <div key={i} style={{
                display: "flex", gap: 10, padding: "8px 0",
                borderBottom: i < d.contactLogs.length - 1 ? "1px solid var(--semi-color-border)" : "none",
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                  background: "var(--semi-color-primary)",
                }} />
                <div style={{
                  flex: 1, padding: "8px 12px", background: "var(--semi-color-bg-0)",
                  borderRadius: 6, fontSize: 13, lineHeight: 1.6,
                }}>
                  {highlightKeywords(log, allKeywords)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
